import {
  FolderIcon,
  FolderMinusIcon,
  FolderPlusIcon,
  IntersectIcon,
  PencilIcon,
  PencilSimpleLineIcon,
  PlusIcon,
  SparkleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { Fragment } from "react";
import { Menu, MenuPopover, MenuSeparator } from "@/components/ui/menu";
import { CustomMenuItem, type MenuItemConfig } from "@/components/ui/menu-item";
import type {
  KeyMessage,
  KeyMessageGroup,
} from "@/store/news-feed/key-messages/key-message.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import {
  mergeKeyMessageGroup,
  updateKeyMessage,
} from "@/store/news-feed/key-messages/key-message-async.actions";

interface KeyMessageGroupHeaderMenuProps {
  keyMessageGroups: KeyMessageGroup[];
  setCreateKeyMessageModalOpen: (b: boolean) => void;
  group: KeyMessageGroup;
  setSelectedGroup: (group: { id: string; name: string } | null) => void;
  feedId?: string;
}

interface KeyMessageFilterMenuProps {
  item: KeyMessage;
  keyMessageGroups: KeyMessageGroup[];
  feedId: string;
  setCreateKeyMessageModalOpen: (b: boolean) => void;
  setCreateKeyMessageGroupModalOpen: (b: boolean) => void;
  setSelectedGroup: (group: { id: string; name: string } | null) => void;
}

const createSubMenuItems = (
  keyMessageGroups: KeyMessageGroup[],
  item: KeyMessage,
  feedId: string,
  setCreateKeyMessageGroupModalOpen: (isOpen: boolean) => void,
  setEditingKeyMessageId: (id: string | null) => void,
) => {
  // Filter out the "Ungrouped" group
  const regularGroups = keyMessageGroups.filter(
    (group) => group.name !== "Ungrouped",
  );
  const ungroupedGroup = keyMessageGroups.find(
    (group) => group.name === "Ungrouped",
  );

  return [
    ...regularGroups.map((group) => ({
      id: group.id,
      name: group.name,
      icon: <FolderIcon className="text-gray-600" />,
      checked: item.groupId === group.id,
      onItemClick: (id: string) => {
        const input = {
          itemId: item.id,
          groupId: item.groupId ? id : null,
          feedId,
        };
        updateKeyMessage(input);
      },
    })),
    // Add the "Ungrouped" group before "New group" if it exists
    ...(ungroupedGroup
      ? [
          {
            id: ungroupedGroup.id,
            name: (
              <span className="italic text-gray-700">
                <span className="text-gray-200">&mdash;</span> None{" "}
                <span className="text-gray-200">&mdash;</span>
              </span>
            ),
            checked: item.groupId === ungroupedGroup.id,
            onItemClick: (id: string) => {
              const input = {
                itemId: item.id,
                groupId: item.groupId ? id : null,
                feedId,
              };
              updateKeyMessage(input);
            },
          },
        ]
      : []),
    {
      id: "new",
      name: "New group",
      icon: <FolderPlusIcon className="text-gray-600" />,
      onItemClick: () => {
        setCreateKeyMessageGroupModalOpen(true);
        setEditingKeyMessageId(item.id);
      },
    },
  ];
};

const KeyMessageGroupHeaderMenu = ({
  keyMessageGroups,
  group,
  feedId,
}: KeyMessageGroupHeaderMenuProps) => {
  const setSelectedGroup = useKeyMessageStore(
    (state) => state.setSelectedGroup,
  );
  const setCreateKeyMessageGroupModalOpen = useKeyMessageStore(
    (state) => state.setCreateKeyMessageGroupModalOpen,
  );
  const setIsEditingKeyMessageGroup = useKeyMessageStore(
    (state) => state.setIsEditingKeyMessageGroup,
  );
  const setKeyMessageGroupName = useKeyMessageStore(
    (state) => state.setKeyMessageGroupName,
  );
  const setCreateKeyMessageModalOpen = useKeyMessageStore(
    (state) => state.setCreateKeyMessageModalOpen,
  );
  const setDeleteKeyMessageOrGroupModalOpen = useKeyMessageStore(
    (state) => state.setDeleteKeyMessageOrGroupModalOpen,
  );
  const setIsDeletingKeyMessageAndGroup = useKeyMessageStore(
    (state) => state.setIsDeletingKeyMessageAndGroup,
  );

  const mergeMenuItems = keyMessageGroups.map((targetGroup) => ({
    id: targetGroup.id,
    name: targetGroup.name,
    icon: <FolderIcon className="text-gray-600" />,
    onItemClick: () => {
      if (feedId) {
        mergeKeyMessageGroup(
          feedId,
          group.id,
          targetGroup.id,
          group.name,
          targetGroup.name,
        );
      }
    },
  }));

  const menuItems: MenuItemConfig[] = [
    {
      id: "rename-group",
      icon: <PencilSimpleLineIcon className="text-gray-600" />,
      label: "Rename group",
      onClick: () => {
        setSelectedGroup(group);
        setCreateKeyMessageGroupModalOpen(true);
        setIsEditingKeyMessageGroup(true);
        setKeyMessageGroupName(group.name);
      },
    },
    {
      id: "new-key-message",
      icon: <PlusIcon className="text-gray-600" />,
      label: "New key topic",
      onClick: () => {
        setCreateKeyMessageModalOpen(true);
        setSelectedGroup(group);
      },
    },
    ...(keyMessageGroups.length > 1
      ? [
          {
            id: "merge-with-group",
            icon: <IntersectIcon className="text-gray-600" />,
            label: "Merge with group",
            subMenu: {
              items: mergeMenuItems,
            },
          },
        ]
      : []),
    {
      id: "delete-group",
      icon: <TrashIcon className="text-red-750" />,
      label: "Delete group",
      onClick: () => {
        setSelectedGroup(group);
        setDeleteKeyMessageOrGroupModalOpen(true);
        setIsDeletingKeyMessageAndGroup("group");
      },
    },
  ];

  return (
    <MenuPopover className="bg-red" placement="left">
      <Menu>
        {menuItems.map((item, index) => (
          <Fragment key={String(item.label)}>
            <CustomMenuItem config={item} />
            {index === menuItems.length - 2 && (
              <MenuSeparator className="mx-1" />
            )}
          </Fragment>
        ))}
      </Menu>
    </MenuPopover>
  );
};

const KeyMessageFilterMenu = ({
  feedId,
  item,
  keyMessageGroups,
}: KeyMessageFilterMenuProps) => {
  const setKeyMessageName = useKeyMessageStore(
    (state) => state.setKeyMessageName,
  );
  const setInsightsKeyMessageId = useKeyMessageStore(
    (state) => state.setInsightsKeyMessageId,
  );
  const setKeyMessageInsightsModalOpen = useKeyMessageStore(
    (state) => state.setKeyMessageInsightsModalOpen,
  );
  const setIsEditingKeyMessage = useKeyMessageStore(
    (state) => state.setIsEditingKeyMessage,
  );
  const setCreateKeyMessageModalOpen = useKeyMessageStore(
    (state) => state.setCreateKeyMessageModalOpen,
  );
  const setSelectedGroup = useKeyMessageStore(
    (state) => state.setSelectedGroup,
  );
  const setSearchTerm = useKeyMessageStore((state) => state.setSearchTerm);
  const setEditingKeyMessageId = useKeyMessageStore(
    (state) => state.setEditingKeyMessageId,
  );
  const setDeleteKeyMessageOrGroupModalOpen = useKeyMessageStore(
    (state) => state.setDeleteKeyMessageOrGroupModalOpen,
  );
  const setIsDeletingKeyMessageAndGroup = useKeyMessageStore(
    (state) => state.setIsDeletingKeyMessageAndGroup,
  );
  const setCreateKeyMessageGroupModalOpen = useKeyMessageStore(
    (state) => state.setCreateKeyMessageGroupModalOpen,
  );

  const subMenuItems = createSubMenuItems(
    keyMessageGroups,
    item,
    feedId,
    setCreateKeyMessageGroupModalOpen,
    setEditingKeyMessageId,
  );
  const keyMessageGroup = keyMessageGroups.find(
    (group) => group.id === item.groupId,
  );
  const menuItems: MenuItemConfig[] = [
    {
      id: "generate-insights",
      icon: <SparkleIcon className={"text-violet-600"} />,
      label: <span className={"text-violet-600"}>Generate Insights</span>,
      onClick: () => {
        setKeyMessageName(item.name);
        setInsightsKeyMessageId(item.id);
        setKeyMessageInsightsModalOpen(true);
      },
      condition: !!item.total,
    },
    {
      id: "edit-key-message",
      icon: <PencilIcon className="text-gray-600" />,
      label: "Edit",
      onClick: () => {
        setIsEditingKeyMessage(true);
        setCreateKeyMessageModalOpen(true);
        setSelectedGroup({
          id: keyMessageGroup ? keyMessageGroup.id : "",
          name: keyMessageGroup ? keyMessageGroup.name : "",
        });
        setKeyMessageName(item.name);
        setSearchTerm(item.searchTerms);
        setEditingKeyMessageId(item.id);
      },
    },
    {
      id: "move-to-group",
      icon: <FolderIcon className="text-gray-600" />,
      label:
        keyMessageGroup?.name !== "Ungrouped"
          ? "Move to group"
          : "Add to group",
      subMenu: {
        items: subMenuItems,
        subtitle: "Groups",
      },
      separator: true,
    },
    ...(keyMessageGroup?.name !== "Ungrouped"
      ? [
          {
            id: "remove-from-group",
            icon: <FolderMinusIcon className="text-gray-600" />,
            label: "Remove from group",
          },
        ]
      : []),
    {
      id: "delete-key-message",
      icon: <TrashIcon className="text-red-750" />,
      label: "Delete key topic",
      onClick: () => {
        setKeyMessageName(item.name);
        setEditingKeyMessageId(item.id);
        setDeleteKeyMessageOrGroupModalOpen(true);
        setIsDeletingKeyMessageAndGroup("message");
      },
    },
  ];

  return (
    <MenuPopover placement="right top" offset={0}>
      <Menu>
        {menuItems.map((menuItem, index) => (
          <Fragment key={String(menuItem.label)}>
            <CustomMenuItem config={menuItem} />
            {index === menuItems.length - 2 && (
              <MenuSeparator className="mx-1" />
            )}
          </Fragment>
        ))}
      </Menu>
    </MenuPopover>
  );
};

KeyMessageFilterMenu.displayName = "KeyMessageFilterMenu";
KeyMessageGroupHeaderMenu.displayName = "KeyMessageGroupHeaderMenu";

export { KeyMessageFilterMenu, KeyMessageGroupHeaderMenu };
